import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/supabase'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables:', {
    url: supabaseUrl ? 'present' : 'missing',
    key: supabaseAnonKey ? 'present' : 'missing'
  })
  throw new Error('Missing Supabase environment variables')
}

// Validate URL format
try {
  new URL(supabaseUrl)
} catch (error) {
  console.error('Invalid Supabase URL format:', supabaseUrl)
  throw new Error('Invalid Supabase URL format')
}

console.log('Supabase configuration:', {
  url: supabaseUrl,
  keyLength: supabaseAnonKey?.length
})

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  global: {
    headers: {
      'Content-Type': 'application/json',
    },
    fetch: async (url, options = {}) => {
      const maxRetries = 3;
      const baseTimeout = 15000; // 15 seconds base timeout
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        let timeoutId: NodeJS.Timeout | null = null;
        
        try {
          const controller = new AbortController();
          timeoutId = setTimeout(() => controller.abort(), baseTimeout * attempt);
          
          const response = await fetch(url, {
            ...options,
            signal: controller.signal,
          });
          
          clearTimeout(timeoutId);
          
          // If successful, return the response
          if (response.ok) {
            return response;
          }
          
          // If it's a server error (5xx) and we have retries left, try again
          if (response.status >= 500 && attempt < maxRetries) {
            console.warn(`Server error ${response.status}, retrying... (attempt ${attempt}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
            continue;
          }
          
          return response;
          
        } catch (error) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          
          if (error instanceof Error && error.name === 'AbortError') {
            if (attempt < maxRetries) {
              console.warn(`Request timeout, retrying... (attempt ${attempt}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
              continue;
            }
            throw new Error('Request timeout - please check your internet connection and try again');
          }
          
          // For other errors, if we have retries left, try again
          if (attempt < maxRetries) {
            console.warn(`Request failed, retrying... (attempt ${attempt}/${maxRetries}):`, error instanceof Error ? error.message : 'Unknown error');
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
            continue;
          }
          
          throw error;
        }
      }
      
      throw new Error('Max retries exceeded');
    }
  },
  db: {
    schema: 'public'
  }
})

// Helper function to get current user's company
export const getCurrentUserCompany = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    if (userError) {
      console.error('Error getting user:', userError)
      return null
    }
    if (!user) return null

    const { data: companyUser, error: companyError } = await supabase
      .from('company_users')
      .select('company_id, companies(*)')
      .eq('user_id', user.id)
      .single()

    if (companyError) {
      console.error('Error getting company:', companyError)
      return null
    }

    return companyUser?.companies || null
  } catch (error) {
    console.error('Error in getCurrentUserCompany:', error)
    return null
  }
}

// Test connection function with better error handling
export const testSupabaseConnection = async () => {
  try {
    console.log('Testing Supabase connection...')
    
    // First test basic connectivity
    const response = await fetch(`${supabaseUrl}/rest/v1/`, {
      method: 'HEAD',
      headers: {
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`
      }
    })

    if (!response.ok) {
      console.error('Supabase endpoint not accessible:', response.status, response.statusText)
      return false
    }

    // Then test database query
    const { error } = await supabase
      .from('companies')
      .select('count')
      .limit(1)

    if (error) {
      console.error('Supabase database query failed:', error)
      return false
    }

    console.log('Supabase connection test successful')
    return true
  } catch (error) {
    console.error('Supabase connection test error:', error)
    return false
  }
}

// Enhanced auth helpers with better error handling
export const signInWithEmail = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    if (error) {
      console.error('Sign in error:', error)
      throw error
    }
    
    return data
  } catch (error) {
    console.error('Sign in failed:', error)
    throw error
  }
}

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut()
    
    if (error) {
      console.error('Sign out error:', error)
      throw error
    }
    
    console.log('Successfully signed out')
  } catch (error) {
    console.error('Sign out failed:', error)
    // Don't throw here - we want to clear local session even if server call fails
    // Just clear the local session
    localStorage.removeItem('supabase.auth.token')
    sessionStorage.removeItem('supabase.auth.token')
  }
}

// Connection status checker
export const checkConnectionStatus = async () => {
  try {
    const response = await fetch(`${supabaseUrl}/rest/v1/`, {
      method: 'HEAD',
      headers: {
        'apikey': supabaseAnonKey,
      },
      signal: AbortSignal.timeout(5000) // 5 second timeout
    })
    
    return response.ok
  } catch (error) {
    console.error('Connection check failed:', error)
    return false
  }
}